import React, { useState, useEffect, useRef } from 'react';
import Container from 'container';
import Swiper from 'react-id-swiper';
import Headline from 'headline';
import ArticleCard from 'shared/article-card';
import Icon from 'icon';

import s from './article_group_2.module.scss';

const demoArticle = {
  title: 'This is the article title',
  date: '2019-05-23T18:38:10',
  image: 'placeholder',
  excerpt: `
    <p>
    Lorem ipsum, dolor sit amet consectetur adipisicing elit. Exercitationem
    ab aliquid accusantium molestiae temporibus in, inventore est nam veniam
    quia neque vel voluptates reiciendis eos alias accusamus id nemo quam
    fugiat porro consectetur nesciunt. Et fuga culpa dolore! Modi reiciendis
    aliquid nostrum illo doloribus reprehenderit eveniet laboriosam blanditiis
    in, accusamus, natus nemo maxime iure, voluptatem ipsum? Odio
  </p>`,
  categories: ['travel'],
  author: 'Joseph Casanova',
  editor: 'Elizabeth Williams',
  illustrator: 'Alexis Michaud'
};

function chunkArray(arr, chunkSize) {
  return Array(Math.ceil(arr.length / chunkSize))
    .fill()
    .map((_, i) => {
      return arr.slice(i * chunkSize, i * chunkSize + chunkSize);
    });
}

function groupArray(arr) {
  return {
    group1: [arr[0]],
    group2: arr.slice(1)
  };
}

const defaultArticlesMap = Array(70)
  .fill(demoArticle)
  .map((article, i) => ({
    ...article,
    title: `${article.title}-${i}`,
    i
  }));

const reduceArticlesMap = (arr, chunk) => {
  const chunkedDefault = chunkArray(arr, chunk);
  return chunkedDefault.map((arr, idx) => groupArray(arr, idx));
};

export default function ArticleGroup2({
  articles = defaultArticlesMap,
  title = '<h4>headline title</h4>'
}) {
  const params = {
    slidesPerView: 1,
    spaceBetween: 10,
    preloadImages: false
  };
  const swiper = useRef();
  const [mappedArticles, setMappedArticles] = useState(
    reduceArticlesMap(articles, 5)
  );
  const [chunkSize, setChunkSize] = useState(5);
  const [ellipses, updateEllipses] = useState(null);

  const updateSwiper = mySwiper => (swiper.current = mySwiper);
  const handleEllipses = totalArticles => {
    const newCount = totalArticles > 1 && Array(totalArticles).fill(null);
    updateEllipses(newCount);
  };

  const handleWindowResize = () => {
    if (window.innerWidth <= 768) {
      return setChunkSize(3);
    }
    return setChunkSize(5);
  };

  const resetSwiper = async chunkSize => {
    const maxArticles = chunkSize * 4;
    const newMappedArticles = reduceArticlesMap(
      articles.slice(0, maxArticles),
      chunkSize
    );
    await setMappedArticles(newMappedArticles);
    await handleEllipses(newMappedArticles.length);
    swiper.current.update();
  };

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    if (swiper && swiper.current) {
      resetSwiper(chunkSize);
    }
  }, [chunkSize, swiper]);
  return (
    <Container className={s.root}>
      <div className={s.wrapper}>
        <Headline h1 title className={s.headline} html={title} />
      </div>
      <Swiper getSwiper={updateSwiper} {...params}>
        {mappedArticles.map(article => (
          <div className={s.cards} key={Math.random()}>
            <div className={s.group}>
              {article.group1.map((card, i) => (
                <ArticleCard
                  {...card}
                  resize
                  key={Math.random()}
                  className={`${s.card} ${s.large}`}
                />
              ))}
            </div>
            <div className={`${s.group} ${s.flex}`}>
              {article.group2.map((card, i) => (
                <ArticleCard
                  {...card}
                  resize
                  key={Math.random()}
                  className={`${s.card} ${s.small}`}
                  slim
                />
              ))}
            </div>
          </div>
        ))}
      </Swiper>
      <Ellipses swiper={swiper} ellipses={ellipses} />
    </Container>
  );
}

function Ellipses({ swiper, ellipses }) {
  const [activeIndex, setActiveIndex] = useState(0);

  const slideTo = idx => swiper && swiper.current.slideTo(idx);
  const handleActive = () => {
    setActiveIndex(swiper.current.activeIndex);
  };

  useEffect(() => {
    if (swiper && swiper.current) {
      swiper.current.on('slideChange', handleActive);
    }
  }, [swiper]);

  useEffect(() => {
    if (swiper && swiper.current) {
      handleActive();
    }
  }, [ellipses]);

  return (
    <div className={`${s.ellipses} ${ellipses ? s.ready : ''}`}>
      {ellipses && (
        <button
          onClick={() => slideTo(activeIndex - 1)}
          type="button"
          className={`${s.ellipse} ${s.ellipseNav} ${s.beginning} ${
            !swiper.current.isBeginning ? s.activeNav : ''
          }`}
        >
          <span className={s.icon}>
            <Icon icon="chevron-left" />
          </span>
        </button>
      )}
      {ellipses &&
        ellipses.map((ellipse, idx) => {
          const active = activeIndex === idx;
          const isLast = ellipses.length - 1 === idx;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <button
              key={idx}
              type="button"
              onClick={() => slideTo(idx)}
              className={`${s.ellipse} ${active ? s.active : ''} ${
                isLast ? s.last : ''
              }`}
            >
              {idx + 1}
            </button>
          );
        })}
      {ellipses && (
        <button
          type="button"
          onClick={() => slideTo(activeIndex + 1)}
          className={`${s.ellipse} ${s.ellipseNav} ${s.end} ${
            !swiper.current.isEnd ? s.activeNav : ''
          }`}
        >
          <span className={s.icon}>
            <Icon icon="chevron-right" />
          </span>
        </button>
      )}
    </div>
  );
}
